import {tryParseUrl} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {findLegacyApplicationAbsoluteUrl} from '../../../helpers/models/legacyApplicationHelpers';
import {openElectronAuthPopup, openExternalUrl} from '../../../services/electron/electronBridgeOutbound';
import {openExternalPopupUrl} from '../../../services/popups';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {navigateToComposer, navigateToFrontIdAsync} from '../../stateless/navigationActions';
import {getCompanyHost} from '../company/companySelectors';
import {getLegacyApplications} from './legacyApplicationsSelectors';

export function legacyApplicationOpenUrlAction(applicationId: number, url: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const application = getLegacyApplications(state)[applicationId];
    if (!application) {
      throw new Error('Application not found.');
    }

    // Try to open a composer first.
    if (navigateToComposer(dispatch, getState, url)) {
      return;
    }

    const host = getCompanyHost(state);
    const absoluteUrl = tryParseUrl(url, host);

    // Try to open an internal ID.
    if (await navigateToFrontIdAsync(dispatch, getState, url, absoluteUrl)) {
      return;
    }

    const applicationUrl = findLegacyApplicationAbsoluteUrl(application, url);
    openInBrowser(applicationUrl);
  };
}

interface LegacyApplicationOpenUrlInPopupOptions {
  width?: number;
  height?: number;
}

export function legacyApplicationOpenUrlInPopupAction(
  applicationId: number,
  url: string,
  options?: LegacyApplicationOpenUrlInPopupOptions,
): ThunkAction {
  return (_dispatch, getState) => {
    const application = getLegacyApplications(getState())[applicationId];
    if (!application) {
      throw new Error('Application not found.');
    }

    const absoluteUrl = findLegacyApplicationAbsoluteUrl(application, url);

    if (isAuthUrl(url) && openElectronAuthPopup(absoluteUrl)) {
      return;
    }

    // Open OAuth authorization URLs directly.
    if (isExternalAuthUrl(url)) {
      openExternalUrl(url);
      return;
    }

    const {width, height} = options || {};

    openExternalPopupUrl(absoluteUrl, width, height);
  };
}

function isAuthUrl(url: string) {
  if (url.startsWith('/oauth/login') || url.startsWith('/auth')) {
    return true;
  }

  const parsedUrl = tryParseUrl(url);
  return (
    parsedUrl &&
    // HQ Plugin
    (parsedUrl.pathname === '/oauth/google-signin' ||
      // Application authentication
      parsedUrl.pathname.startsWith('/accounts/oauth/applications'))
  );
}

/**
 * Integrations retrieve an OAuth authorization URL prior to
 * opening a popup.
 *
 * These URLs are identified by a specific query parameter.
 */
function isExternalAuthUrl(url: string) {
  const parsedUrl = tryParseUrl(url);

  return parsedUrl?.searchParams.get('is_external_auth_url') === '1';
}
